import { Injectable, inject } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../../../../core/services/api.service';
import {
  CreateLiveCallAuthPayload,
  LiveCallAuthConfig,
  LiveCallAuthListResponse,
  PatchLiveCallAuthPayload,
} from './live-call-auth.models';

/**
 * HTTP client for the live-call universal auth catalog.
 *
 * ── BACKEND CONTRACT (prism-backend-nodejs, live-call-management/agent) ─────
 *   GET    /live-call-agent/auth?includeInactive=0|1
 *            → { count, authConfigs: LiveCallAuthConfig[] }   (tokens masked)
 *   POST   /live-call-agent/auth
 *            body: { name, base_url, auth_type, token?, is_active? }
 *            → { authConfig }   (409 if name exists; always saved universal)
 *   PATCH  /live-call-agent/auth/:id
 *            body: { name?, base_url?, auth_type?, token? (null clears), is_active? }
 *            → { authConfig }
 *   DELETE /live-call-agent/auth/:id
 *            → { deleted: boolean }   (tools referencing it are detached)
 *
 * Product-agnostic AND environment-agnostic: every entry is universal (for_env=3).
 */
@Injectable({
  providedIn: 'root',
})
export class LiveCallAuthService {
  private api = inject(ApiService);

  /** Suppress ApiService's global toaster/loader — the tab renders its own states. */
  private readonly quiet = { showErrorToaster: false, showLoader: false } as const;

  /** GET the auth catalog. `includeInactive=false` keeps only selectable entries. */
  listAuthConfigs(includeInactive = true): Observable<LiveCallAuthListResponse> {
    const params: Record<string, string | number> = {};
    if (!includeInactive) params['includeInactive'] = 0;
    return this.api
      .get<LiveCallAuthListResponse>('/live-call-agent/auth', params, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** POST a new entry — 409 if the name is taken. */
  createAuthConfig(payload: CreateLiveCallAuthPayload): Observable<{ authConfig: LiveCallAuthConfig }> {
    return this.api
      .post<{ authConfig: LiveCallAuthConfig }>('/live-call-agent/auth', payload, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** PATCH entry fields; `token: null` clears the secret, omitted keeps it. */
  patchAuthConfig(authId: number, payload: PatchLiveCallAuthPayload): Observable<{ authConfig: LiveCallAuthConfig }> {
    return this.api
      .patch<{ authConfig: LiveCallAuthConfig }>(`/live-call-agent/auth/${authId}`, payload, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** DELETE (soft) one entry. Tools referencing it are detached, not deleted. */
  deleteAuthConfig(authId: number): Observable<{ deleted: boolean }> {
    return this.api
      .delete<{ deleted: boolean }>(`/live-call-agent/auth/${authId}`, this.quiet)
      .pipe(catchError(this.handleError));
  }

  /** Normalize ApiService errors to the `{status?, message}` the tab renders. */
  private handleError = (error: any): Observable<never> => {
    const message =
      error?.message ||
      error?.error ||
      error?.err ||
      error?.detail ||
      'An unexpected error occurred';
    return throwError(() => ({ status: error?.statusCode ?? error?.status, message, raw: error }));
  };
}
